import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowLeft, Mail, MessageCircle, Send, Loader2, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { z } from 'zod';

const feedbackSchema = z.object({
  subject: z.string().trim().min(1, 'Subject is required').max(200, 'Subject must be under 200 characters'),
  message: z.string().trim().min(1, 'Message is required').max(2000, 'Message must be under 2000 characters'),
  category: z.string().min(1),
});

const categories = [
  { value: 'general', label: 'General Feedback' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'billing', label: 'Billing / Subscription' },
  { value: 'bank-sync', label: 'Bank Sync Issue' },
];

const faqs = [
  {
    question: 'How do I connect my bank account?',
    answer: 'Go to More → Bank Sync and follow the prompts to securely connect your bank. This feature requires an active subscription.',
  },
  {
    question: 'Is my financial data secure?',
    answer: 'Yes! Your data is encrypted both in transit and at rest. Bank connections use Basiq, a trusted Open Banking provider regulated by Australian authorities.',
  },
  {
    question: 'How do I add a transaction manually?',
    answer: 'Tap the + button on the Transactions page, enter the details, and save. You can also edit or delete transactions by tapping on them.',
  },
  {
    question: 'What counts as a tax-deductible expense?',
    answer: 'When adding or editing a transaction, toggle "Tax deductible" to mark it. Common deductions include work-related expenses, donations, and home office costs. Consult a tax professional for specific advice.',
  },
  {
    question: 'How do I set up a budget?',
    answer: 'Navigate to the Budget tab and tap "Add Budget" to set monthly spending limits by category. The app will track your progress automatically.',
  },
  {
    question: 'Can I export my data for tax time?',
    answer: 'Yes! Go to More → Tax Wizard to generate a tax summary, or More → Reports to export detailed transaction reports as PDF.',
  },
  {
    question: 'How do I cancel my subscription?',
    answer: 'Go to More → Settings and tap "Manage Subscription" to access your billing portal where you can cancel or modify your plan.',
  },
  {
    question: 'Why are my synced transactions showing $0 totals?',
    answer: 'The Dashboard shows the current month only. If your synced transactions are from a different period, check the Transactions page for the full history.',
  },
];

export default function SupportPage() {
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async () => {
    setErrors({});
    const result = feedbackSchema.safeParse({ subject, message, category });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(e => {
        if (e.path[0]) fieldErrors[e.path[0] as string] = e.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-feedback', {
        body: { subject: result.data.subject, message: result.data.message, category: result.data.category },
      });

      if (error) throw error;

      setSent(true);
      setSubject('');
      setMessage('');
      setCategory('general');
      toast.success('Feedback sent — thank you!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to send feedback. Please try again or email us directly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-4 pt-6 pb-24">
      <div className="mb-6 flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="font-display text-2xl font-bold">Help & Support</h1>
      </div>

      {/* Feedback Form */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Send className="h-5 w-5 text-primary" />
            Send Feedback
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle className="h-10 w-10 text-success" />
              <p className="font-medium">Thanks for your feedback!</p>
              <p className="text-sm text-muted-foreground">We'll get back to you if needed.</p>
              <Button variant="outline" size="sm" onClick={() => setSent(false)}>
                Send another
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Subject</Label>
                <Input
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="Brief summary of your feedback"
                  maxLength={200}
                  className="h-10"
                />
                {errors.subject && <p className="mt-1 text-xs text-destructive">{errors.subject}</p>}
              </div>

              <div>
                <Label>Message</Label>
                <Textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Tell us more..."
                  maxLength={2000}
                  rows={4}
                />
                {errors.message && <p className="mt-1 text-xs text-destructive">{errors.message}</p>}
                <p className="mt-1 text-right text-xs text-muted-foreground">{message.length}/2000</p>
              </div>

              <Button onClick={handleSubmit} disabled={sending} className="w-full gap-2">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? 'Sending...' : 'Send Feedback'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact Section */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageCircle className="h-5 w-5 text-primary" />
            Contact Us
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Prefer email? Reach us directly.
          </p>
          <Button asChild variant="outline" className="w-full gap-2">
            <a href="mailto:inceptionglen@gmail.com?subject=AussiBooks%20Support">
              <Mail className="h-4 w-4" />
              Email Support
            </a>
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            inceptionglen@gmail.com
          </p>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`}>
                <AccordionTrigger className="text-left text-sm">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
